<script lang="ts" generics="T">
  import Honeybadger from "$app/utils/honeybadger"
  import { appStore } from "$app/store.svelte"
  import { emojis } from "$app/constants"
  import { fade } from "svelte/transition"
  import { ThumbsUp } from "lucide-svelte"
  import { xhr } from "$app/utils"

  interface Props {
    reactionable: T & { id: number }
    reactionable_type: "Recognition" | "ForumPost" | "RecognitionComment"
    reaction: ReactionType
    url?: any
    css?: string
    children?: import("svelte").Snippet
    oncreate?: (react: ReactionType) => void
    onupdate?: (react: ReactionType) => void
    ondelete?: (react: ReactionType) => void
  }

  let {
    reactionable,
    reactionable_type,
    reaction,
    url = `/api/v1/plans/${appStore.plan.id}/reactions`,
    css = "",
    children,
    oncreate,
    onupdate,
    ondelete
  }: Props = $props()

  function toggleReaction(emoji: string) {
    if (!reaction.emoji) {
      createReaction(emoji)
      return
    }

    if (reaction.emoji === emoji) {
      deleteReaction()
      return
    }

    updateReaction(emoji)
  }

  let saving = false

  async function updateReaction(emoji: string) {
    try {
      saving = true
      const resp = await xhr.patch(`${url}/${reaction.id}.json`, {
        reaction: {
          emoji: emoji,
          reactionable_type: reactionable_type,
          reactionable_id: reactionable.id
        }
      })
      const react = resp.data as T

      hover = false
      onupdate?.(react)
    } catch (e) {
      Honeybadger.notify(e)
    } finally {
      saving = false
    }
  }

  async function createReaction(emoji: string) {
    try {
      saving = true
      const resp = await xhr.post(`${url}.json`, {
        reaction: {
          emoji: emoji,
          reactionable_type: reactionable_type,
          reactionable_id: reactionable.id
        }
      })
      const react = resp.data as T

      hover = false
      oncreate?.(react)
    } catch (e) {
      Honeybadger.notify(e)
    } finally {
      saving = false
    }
  }

  async function deleteReaction() {
    try {
      saving = true
      const resp = await xhr.delete(`${url}/${reaction.id}.json`)

      const react = resp.data as T
      hover = false
      ondelete?.(react)
    } catch (e) {
      Honeybadger.notify(e)
    } finally {
      saving = false
    }
  }

  function mouseLeave(e: MouseEvent) {
    if (container?.contains(e.relatedTarget as Node)) return

    hover = false
  }

  let hover = $state(false)
  let container: null | HTMLElement = $state(null)
</script>

<div class="relative">
  <button
    data-name="like-button"
    class="{reaction.id
      ? 'text-blue-600 font-bold'
      : 'text-gray-900'} flex justify-center items-center hover:bg-gray-100 {css}"
    onmouseenter={() => (hover = true)}
    onmouseleave={mouseLeave}
  >
    {#if children}{@render children()}{:else}
      <ThumbsUp class="size-5 mr-2" />
      Like
    {/if}
  </button>
  {#if hover}
    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <div
      class="absolute z-50 cont h-14"
      bind:this={container}
      transition:fade
      onmouseleave={() => (hover = false)}
    >
      <!-- svelte-ignore a11y_no_static_element_interactions -->
      <div
        class="flex items-center gap-2 bg-core rounded-full shadow-md p-2 border w-auto"
        onmouseleave={() => (hover = false)}
      >
        {#each emojis as emoji, i (emoji.name)}
          <button
            class="w-8 emoji reaction-{emoji.name}"
            style="--delay: {i * 0.03}s"
            onclick={() => toggleReaction(emoji.name)}
            type="button"
          >
            <span class="legend-reaction">{emoji.label}</span>
            <img src={emoji.url} alt={emoji.label} class="size-8" />
          </button>
        {/each}
      </div>
    </div>
  {/if}
</div>

<style>
  .cont {
    top: -54px;
    left: 50%;
    transform: translateX(-50%);
  }
  .legend-reaction {
    background: rgba(0, 0, 0, 0.75);
    border-radius: 10px;
    box-sizing: border-box;
    color: #fff;
    display: inline-block;
    font-size: 8px;
    font-weight: bold;
    line-height: 14px;
    max-width: 100%;
    opacity: 0;
    overflow: hidden;
    padding: 0 6px;
    white-space: nowrap;
    transition: opacity 50ms ease;
    left: 50%;
    position: absolute;
    text-align: center;
    top: -20px;
    transform: translateX(-50%);
    width: content;
    min-width: max-content;
  }

  button.emoji:hover,
  button.emoji:focus {
    transition: all 0.2s ease-in;
    transform: scale(1.5) translateY(-5px);
  }

  button.emoji:hover .legend-reaction,
  button.emoji:focus .legend-reaction {
    opacity: 1;
  }

  .emoji img {
    transform: translateY(30px);
    animation: appear 0.2s ease-in-out;
    animation-delay: var(--delay);
    animation-fill-mode: forwards;
  }

  @keyframes appear {
    0% {
      transform: translateY(30px);
    }
    100% {
      transform: translateY(0px);
    }
  }
</style>
